import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Alert,
  Divider
} from '@mui/material';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import GoogleIcon from '@mui/icons-material/Google';

const TabPanel = styled(Box)`
  padding: 16px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoadingButton = styled(Button)`
  position: relative;
  
  .loading-indicator {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -12px;
    margin-left: -12px;
  }
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 16px;
`;

const DemoButton = styled(Button)`
  margin-top: 12px;
  width: 100%;
  background-color: #4CAF50;
  &:hover {
    background-color: #388E3C;
  }
`;

const GoogleButton = styled(Button)`
  margin-top: 12px;
  width: 100%;
  background-color: #ffffff;
  color: #757575;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DividerContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0;
`;

const StyledDivider = styled(Divider)`
  flex-grow: 1;
`;

const DividerText = styled(Typography)`
  padding: 0 10px;
  color: rgba(255, 255, 255, 0.6);
`;

/**
 * AuthDialog Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 */
const AuthDialog = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { signIn, signUp, loginWithDemo, signInWithGoogle, isDemoMode } = useAuth();

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (tab === 0) {
        // Login
        await signIn(email, password);
      } else {
        // Register
        if (!displayName.trim()) {
          throw new Error('Display name is required');
        }
        await signUp(email, password, displayName);
      }
      
      setLoading(false);
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('Authentication error:', error);
      
      // Set user-friendly error message
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('Email is already in use');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email format');
      } else {
        setError(error.message || 'An error occurred during authentication');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    try {
      await signInWithGoogle();
      setLoading(false);
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('Google login error:', error);
      setError('Failed to sign in with Google');
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      await loginWithDemo();
      setLoading(false);
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('Demo login error:', error);
      setError('Failed to log in with demo account');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Login" />
          <Tab label="Register" />
        </Tabs>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <StyledAlert severity="error">{error}</StyledAlert>
        )}

        {/* Google Sign In Button */}
        <Box mb={2}>
          <GoogleButton 
            variant="contained" 
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<GoogleIcon />}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign in with Google'}
          </GoogleButton>
        </Box>

        {isDemoMode && (
          <Box mb={3}>
            <DemoButton 
              variant="contained" 
              fullWidth
              onClick={handleDemoLogin}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Log in with Demo Account'}
            </DemoButton>
          </Box>
        )}
        
        <DividerContainer>
          <StyledDivider />
          <DividerText variant="body2">or</DividerText>
          <StyledDivider />
        </DividerContainer>
        
        <TabPanel hidden={tab !== 0}>
          <Form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </Form>
        </TabPanel>
        
        <TabPanel hidden={tab !== 1}>
          <Form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              helperText="Password must be at least 6 characters"
            />
            <TextField
              label="Display Name"
              fullWidth
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={loading}
            />
          </Form>
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading && (
            <CircularProgress size={24} color="inherit" className="loading-indicator" />
          )}
          {tab === 0 ? 'Login' : 'Register'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AuthDialog;
